import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTasks } from '@/hooks/useTasks';
import { useCommesse } from '@/hooks/useCommesse';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  Calendar, Users, Plus, Trash2, CheckCircle2, Clock, AlertTriangle, LogOut,
  Crown, Swords, Shield, Target, Flame, Skull, Send, Timer, UserPlus,
  Link as LinkIcon, Monitor, FileText, ExternalLink, X, Check, ChevronsUpDown, CalendarDays, Kanban as KanbanIcon,
  ListTodo, PauseCircle, CalendarRange
} from 'lucide-react';
import { Handshake } from 'lucide-react';
import { format, isToday, isTomorrow, isPast, isFuture, parseISO, startOfDay } from 'date-fns';
import { it } from 'date-fns/locale';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

import { API } from '@/config/api';
import PendingReasonModal from '@/components/PendingReasonModal';
import ScheduleCalendar from '@/components/ScheduleCalendar';
import { toast } from 'sonner';

const MANAGER_QUOTES = [
  "\"Il potere logora chi non ce l'ha\" - Giulio Andreotti 🇮🇹",
  "\"L'important c'est pas la chute, c'est l'atterrissage\" - La Haine 🇫🇷",
  "\"Fai loro un'offerta che non possono rifiutare... avec élégance\" - Don Doit",
  "\"Un vrai boss parle poco e fa molto\" - Saggezza Italo-Francese 🤌🇫🇷",
  "\"Je suis le Don, donc je commande\" - Il Padrino di Parigi",
];

const TaskCard = ({ task, onDelete, onUpdateStatus, onPause, onEdit, index, isCompleted = false, minimal = false }) => {
  const isExpired = task.due_date && isPast(parseISO(task.due_date)) && !isToday(parseISO(task.due_date)) && task.status !== 'completed' && task.status !== 'waiting';

  const CardWrapper = ({ children }) => {
    return (
      <Draggable draggableId={task.id} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className={cn(snapshot.isDragging && "opacity-50")}
          >
            {children}
          </div>
        )}
      </Draggable>
    );
  };

  const getGmailLink = (taskTitle) => {
    const query = encodeURIComponent(`subject:(${taskTitle})`);
    return `https://mail.google.com/mail/u/0/#search/${query}`;
  };

  return (
    <CardWrapper>
      <Card
        onClick={() => onEdit && onEdit(task)}
        className={cn(
          "bg-zinc-900 overflow-hidden shadow-xl transition-all duration-300 border-zinc-800 cursor-pointer group",
          !isCompleted && "hover:border-amber-500/50 hover:shadow-amber-500/5",
          isExpired && "border-red-500/50 shadow-red-500/10",
          isCompleted && "opacity-60 grayscale-[0.5]"
        )}>
        {!minimal && (
          <div className={cn(
            "h-1.5 transition-colors",
            task.status === 'pending' ? 'bg-red-500' :
              task.status === 'in_progress' ? 'bg-amber-500' :
              task.status === 'waiting' ? 'bg-blue-500' : 'bg-green-500',
            isExpired && "animate-pulse"
          )} />
        )}
        <CardContent className={cn("p-5 flex flex-col h-full", minimal && "p-4")}>
          <div className="flex items-start justify-between mb-3">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Badge className={cn(
                  "text-[10px] w-fit font-black tracking-tighter",
                  task.status === 'pending' ? 'bg-red-500/20 text-red-400' :
                    task.status === 'in_progress' ? 'bg-amber-500/20 text-amber-400' :
                    task.status === 'waiting' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-green-500/20 text-green-400'
                )}>
                  {task.status === 'pending' ? 'IN ATTESA' :
                    task.status === 'in_progress' ? 'IN CORSO' :
                    task.status === 'waiting' ? 'IN PAUSA' : 'COMPLETATO'}
                </Badge>
                {isExpired && (
                  <Badge className="bg-red-600 text-white text-[10px] animate-pulse">SCADUTO</Badge>
                )}
              </div>
              {task.status === 'waiting' && task.pending_reason && (
                <div className="flex items-center gap-1 mt-0.5">
                  <PauseCircle className="w-3 h-3 text-blue-400/70 flex-shrink-0" />
                  <span className="text-[10px] text-blue-300/70 italic">{task.pending_reason}</span>
                </div>
              )}
              <span className="text-xs font-bold text-amber-500 flex items-center gap-1">
                <Users className="w-3 h-3" />
                {task.assigned_to_name}
              </span>
            </div>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); onDelete(task.id); }}
                className="h-8 w-8 p-0 text-zinc-600 hover:text-red-500 hover:bg-red-500/10 -mt-1 -mr-1"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <h4 className={cn(
            "font-bold text-white text-lg mb-2 leading-tight group-hover:text-amber-400 transition-colors",
            isCompleted && "line-through text-zinc-500",
            minimal && "text-base"
          )}>
            {task.title}
          </h4>

          {task.description && !minimal && (
            <p className="text-xs text-zinc-400 mb-4 bg-black/40 p-2 rounded border-l-2 border-zinc-700 line-clamp-2">
              {task.description}
            </p>
          )}

          <div className="mt-auto flex items-center justify-between pt-2">
            <div className="flex flex-col gap-2">
              {task.due_date && task.status !== 'waiting' && (
                <div className={cn(
                  "flex items-center gap-1.5 font-black text-[10px] uppercase",
                  isExpired ? "text-red-500" : "text-zinc-500"
                )}>
                  <Timer className="w-3.5 h-3.5" />
                  {format(parseISO(task.due_date), 'dd MMM', { locale: it })}
                </div>
              )}

              {/* Gmail Link */}
              <a
                href={getGmailLink(task.title)}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1.5 text-[10px] text-zinc-500 hover:text-amber-400 transition-colors uppercase font-bold"
              >
                <Send className="w-3 h-3" />
                Vedi in Gmail
              </a>
            </div>

            {!isCompleted && !minimal && (
              <div className="flex gap-1">
                {task.status === 'in_progress' && onPause && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-[10px] h-7 font-black border-red-500/30 text-red-400 hover:bg-red-500/10"
                    onClick={(e) => { e.stopPropagation(); onPause(task); }}
                  >
                    <PauseCircle className="w-3 h-3 mr-1" />
                    PAUSA
                  </Button>
                )}
                <Button
                  size="sm"
                  variant={(task.status === 'pending' || task.status === 'waiting') ? 'outline' : 'default'}
                  className={cn(
                    "text-[10px] h-7 font-black",
                    (task.status === 'pending' || task.status === 'waiting')
                      ? "border-zinc-800 text-zinc-400 hover:bg-zinc-800"
                      : "bg-green-600 hover:bg-green-500 text-white"
                  )}
                  onClick={(e) => { e.stopPropagation(); onUpdateStatus(task.id, (task.status === 'pending' || task.status === 'waiting') ? 'in_progress' : 'completed'); }}
                >
                  {task.status === 'pending' ? 'INIZIA →' : task.status === 'waiting' ? 'RIPRENDI →' : 'CHIUDI 🤌'}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </CardWrapper>
  );
};

const ManagerDashboard = () => {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Authentication Guard
  useEffect(() => {
    if (!authLoading && (!user || (user.role !== 'manager' && user.role !== 'admin'))) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  const {
    tasks,
    pendingNegotiations,
    createTask,
    updateTask: apiUpdateTask,
    updateTaskStatus,
    deleteTask,
    approveNegotiation,
    rejectNegotiation
  } = useTasks();

  console.log('ManagerDashboard Initialized - apiUpdateTask type:', typeof apiUpdateTask);
  const { commesse } = useCommesse();
  const [members, setMembers] = useState([]);
  const [showNewTask, setShowNewTask] = useState(false);
  const [isPreviewingTask, setIsPreviewingTask] = useState(false);
  const [showNewMember, setShowNewMember] = useState(false);
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' or 'planning'
  const [newMember, setNewMember] = useState({ name: '', email: '' });

  // Multi-assign state
  const [openCombobox, setOpenCombobox] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]); // List of IDs

  const [newTask, setNewTask] = useState({
    assigned_to: '', // Keep for legacy/single compatibility but we'll primarily use ids now
    assigned_to_ids: [],
    assigned_to_name: '',
    title: '',
    description: '',
    due_date: '',
    commessa_hint: '',
    links: []
  });
  const [linkInput, setLinkInput] = useState({ url: '', label: '' });
  const [quote] = useState(MANAGER_QUOTES[Math.floor(Math.random() * MANAGER_QUOTES.length)]);
  const [managerResponse, setManagerResponse] = useState('');

  // Editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [pausingTask, setPausingTask] = useState(null);
  const [emailInput, setEmailInput] = useState('');

  const [stats, setStats] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [membersRes, statsRes] = await Promise.all([
        axios.get(`${API}/users/members`),
        axios.get(`${API}/stats/dashboard`)
      ]);
      setMembers(Array.isArray(membersRes.data) ? membersRes.data : []);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateMember = async () => {
    if (!newMember.name || !newMember.email) return;
    try {
      await axios.post(`${API}/users/create-member`, newMember);
      setShowNewMember(false);
      setNewMember({ name: '', email: '' });
      fetchMembers(); // Refresh members list
    } catch (err) {
      console.error('Error creating member:', err);
      alert(err.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const toggleMemberSelection = (memberId) => {
    setSelectedMembers(prev => {
      if (prev.includes(memberId)) {
        return prev.filter(id => id !== memberId);
      } else {
        return [...prev, memberId];
      }
    });
  };

  const handleCreateTask = async () => {
    // Determine assignees (support legacy single assign or new multi assign)
    let assignees = selectedMembers.length > 0 ? selectedMembers : (newTask.assigned_to ? [newTask.assigned_to] : []);

    if (assignees.length === 0 || !newTask.title) return;

    // Create the task payload
    // If multiple assignees, backend handles iteration
    const payload = {
      ...newTask,
      assigned_to_ids: assignees,
      // Pass first assignee as default for legacy compatibility if needed
      assigned_to: assignees[0],
      assigned_to_name: members.find(m => m.id === assignees[0])?.name || 'Unknown',
      assigned_by: user.id,
      assigned_by_name: user.name
    };

    const result = await createTask(payload);

    if (result.success) {
      setShowNewTask(false);
      setIsPreviewingTask(false);
      // Reset state
      setNewTask({ assigned_to: '', assigned_to_ids: [], assigned_to_name: '', title: '', description: '', due_date: '', commessa_hint: '', links: [], emails: [] });
      setSelectedMembers([]);
      if (result.message) {
        // Optional toast or alert here
        // alert(result.message);
      }
    }
  };

  const addLink = () => {
    if (!linkInput.url) return;
    setNewTask(prev => ({
      ...prev,
      links: [...(prev.links || []), `${linkInput.label || 'Link'}|${linkInput.url}`]
    }));
    setLinkInput({ url: '', label: '' });
  };

  const removeLink = (index) => {
    setNewTask(prev => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index)
    }));
  };

  // Helper for planning view
  const groupTasksByDate = (taskList) => {
    const groups = {
      expired: [],
      today: [],
      tomorrow: [],
      week: [],
      future: [],
      noDate: []
    };

    taskList.forEach(task => {
      // Only show incomplete tasks in planning view usually, but we can include all or filter
      if (task.status === 'completed') return;

      if (!task.due_date) {
        groups.noDate.push(task);
        return;
      }

      const due = parseISO(task.due_date);
      const today = startOfDay(new Date());

      if (isPast(due) && !isToday(due)) {
        groups.expired.push(task);
      } else if (isToday(due)) {
        groups.today.push(task);
      } else if (isTomorrow(due)) {
        groups.tomorrow.push(task);
      } else {
        // Simple check for "rest of week" vs future could be more complex, keeping it simple
        groups.future.push(task);
      }
    });
    return groups;
  };

  const getAssigneeNames = () => {
    if (selectedMembers.length === 0) return '';
    if (selectedMembers.length === 1) return members.find(m => m.id === selectedMembers[0])?.name;
    return `${selectedMembers.length} Soldati Selezionati`;
  };

  const getGmailLink = (taskTitle) => {
    const query = encodeURIComponent(`subject:(${taskTitle})`);
    return `https://mail.google.com/mail/u/0/#search/${query}`;
  };

  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    // Map column IDs to backend status
    const statusMap = {
      'pending': 'pending',
      'in_progress': 'in_progress',
      'completed': 'completed'
    };

    const newStatus = statusMap[destination.droppableId];
    if (newStatus) {
      await updateTaskStatus(draggableId, newStatus);
      return;
    }

    // Handle date updates for planning view
    const today = new Date();
    let newDate = null;

    switch (destination.droppableId) {
      case 'today':
        newDate = format(today, 'yyyy-MM-dd');
        break;
      case 'tomorrow':
        const tomorrow = new Date();
        tomorrow.setDate(today.getDate() + 1);
        newDate = format(tomorrow, 'yyyy-MM-dd');
        break;
      case 'week':
        const nextWeek = new Date();
        nextWeek.setDate(today.getDate() + 4); // Roughly next days
        newDate = format(nextWeek, 'yyyy-MM-dd');
        break;
      case 'future':
        const future = new Date();
        future.setDate(today.getDate() + 14);
        newDate = format(future, 'yyyy-MM-dd');
        break;
      case 'noDate':
        newDate = null;
        break;
      default:
        return;
    }

    await apiUpdateTask(draggableId, { due_date: newDate });
  };

  const handlePauseConfirm = async (reason) => {
    const res = await updateTaskStatus(pausingTask.id, 'waiting', reason);
    if (res.success) {
      toast.success("⏸️ ORDINE IN PAUSA", { description: `"${reason}"` });
    }
    setPausingTask(null);
  };

  const handleEditClick = (task) => {
    setEditingTask({ ...task, emails: task.emails || [] });
    setIsEditing(true);
  };

  const handleUpdateTask = async () => {
    if (!editingTask) {
      console.warn("handleUpdateTask: No editingTask in state.");
      return;
    }

    console.log("handleUpdateTask: calling apiUpdateTask for ID", editingTask.id);
    if (typeof apiUpdateTask !== 'function') {
      console.error("CRITICAL: apiUpdateTask is not a function! Check useTasks.js exports.");
      toast.error("Errore di sistema: Funzione di aggiornamento non trovata.");
      return;
    }

    const result = await apiUpdateTask(editingTask.id, editingTask);
    if (result.success) {
      setIsEditing(false);
      setEditingTask(null);
      // Optional: success toast could be added here
    } else {
      console.error("Update failed:", result.error);
    }
  };

  const addEmail = (isEdit = false) => {
    if (!emailInput) return;
    if (isEdit) {
      setEditingTask(prev => ({
        ...prev,
        emails: [...(prev.emails || []), emailInput]
      }));
    } else {
      setNewTask(prev => ({
        ...prev,
        emails: [...(prev.emails || []), emailInput]
      }));
    }
    setEmailInput('');
  };

  const removeEmail = (index, isEdit = false) => {
    if (isEdit) {
      setEditingTask(prev => ({
        ...prev,
        emails: prev.emails.filter((_, i) => i !== index)
      }));
    } else {
      setNewTask(prev => ({
        ...prev,
        emails: prev.emails.filter((_, i) => i !== index)
      }));
    }
  };

  const safeTasks = Array.isArray(tasks) ? tasks : [];
  const pendingTasks = safeTasks.filter(t => t.status === 'pending' || t.status === 'waiting');
  const inProgressTasks = safeTasks.filter(t => t.status === 'in_progress');
  const completedTasks = safeTasks.filter(t => t.status === 'completed');

  const prevNegsRef = useRef([]);

  useEffect(() => {
    if (pendingNegotiations.length > prevNegsRef.current.length && prevNegsRef.current.length > 0) {
      // Find new ones
      const newNegs = pendingNegotiations.filter(n => !prevNegsRef.current.find(pn => pn.id === n.id));

      newNegs.forEach(neg => {
        if (Notification.permission === "granted") {
          new Notification("TRATTATIVA IN CORSO 🤌", {
            body: `${neg.user_name} chiede ${neg.days_requested} giorni per "${neg.task_title}"`,
            icon: "/favicon.png"
          });
        }

        try {
          const audio = new Audio('/sounds/notification.mp3');
          audio.play();
        } catch (e) { }
      });
    }
    prevNegsRef.current = pendingNegotiations;
  }, [pendingNegotiations]);

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="font-display text-xl font-bold flex items-center gap-2">
              <span className="text-2xl">👑🇫🇷</span>
              <span className="gold-text">Don Doit</span>
            </h1>
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
              Manager
            </Badge>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-zinc-400 hidden md:block">{user?.name}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/operations')}
              className="border-zinc-700 text-zinc-300 hover:text-white"
            >
              Operations
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-zinc-400 hover:text-red-400"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-6xl mx-auto p-4">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-amber-950 via-zinc-900 to-zinc-950 p-8 mb-8 border border-amber-500/20">
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl" />

          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <Crown className="w-8 h-8 text-amber-400" />
              <Crown className="w-6 h-6 text-amber-400/60" />
            </div>

            <h1 className="font-display text-4xl md:text-5xl font-black mb-4">
              <span className="text-white">Benvenuto, </span>
              <span className="text-amber-400">{user?.name}</span>
              <span className="text-3xl ml-2">🤌</span>
            </h1>

            <p className="text-zinc-400 text-lg italic max-w-xl">
              {quote}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mt-8">
              <div className="bg-black/30 backdrop-blur rounded-2xl p-4 border border-white/10">
                <div className="flex items-center gap-2 text-red-400 mb-1">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="text-xs uppercase tracking-wider">In Attesa</span>
                </div>
                <p className="text-3xl font-black text-white">{pendingTasks.length}</p>
              </div>
              <div className="bg-black/30 backdrop-blur rounded-2xl p-4 border border-white/10">
                <div className="flex items-center gap-2 text-amber-400 mb-1">
                  <Clock className="w-5 h-5" />
                  <span className="text-xs uppercase tracking-wider">In Corso</span>
                </div>
                <p className="text-3xl font-black text-white">{inProgressTasks.length}</p>
              </div>
              <div className="bg-black/30 backdrop-blur rounded-2xl p-4 border border-white/10">
                <div className="flex items-center gap-2 text-green-400 mb-1">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="text-xs uppercase tracking-wider">Completati</span>
                </div>
                <p className="text-3xl font-black text-white">{completedTasks.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Pending Negotiations Section */}
        {pendingNegotiations && Array.isArray(pendingNegotiations) && pendingNegotiations.length > 0 && (
          <div className="mb-8 overflow-hidden rounded-2xl bg-zinc-900/50 border border-amber-500/30">
            <div className="p-4 border-b border-amber-500/20 bg-amber-950/20 flex items-center gap-2">
              <Handshake className="w-5 h-5 text-amber-400" />
              <h3 className="font-display font-bold text-white uppercase tracking-wider text-sm">Trattative in Attesa di Approvazione</h3>
              <Badge className="ml-auto bg-amber-500 text-black font-bold">
                {pendingNegotiations.length}
              </Badge>
            </div>
            <div className="p-4 grid gap-4 grid-cols-1 md:grid-cols-2">
              {pendingNegotiations.map((neg) => (
                <div key={neg.id} className="bg-black/40 rounded-xl p-4 border border-zinc-800 flex flex-col gap-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-bold text-white text-sm">{neg.task_title}</h4>
                      <p className="text-xs text-zinc-500 mt-1">Richiesta da: <span className="text-amber-400">{neg.user_name}</span></p>
                    </div>
                    <Badge variant="outline" className="border-amber-500/30 text-amber-400 text-[10px]">
                      +{neg.days_requested} GIORN{neg.days_requested === 1 ? 'O' : 'I'}
                    </Badge>
                  </div>

                  <div className="bg-zinc-800/30 rounded-lg p-3 text-xs italic text-zinc-400">
                    "Offro {neg.offer_type} in cambio di {neg.days_requested} giorni..."
                  </div>

                  <div className="flex gap-2 mt-auto">
                    <Button
                      size="sm"
                      className="flex-1 bg-green-600 hover:bg-green-500 h-8 text-xs font-bold"
                      onClick={() => approveNegotiation(neg.id, user.id)}
                    >
                      APPROVA
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 text-xs font-bold"
                      onClick={() => rejectNegotiation(neg.id, user.id)}
                    >
                      RIFIUTA
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mb-8 flex flex-wrap gap-3">
          {/* New Task Dialog */}
          <Dialog open={showNewTask} onOpenChange={setShowNewTask}>
            <DialogTrigger asChild>
              <Button
                size="lg"
                className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white font-bold px-8 py-6 rounded-xl shadow-lg shadow-amber-500/20"
                data-testid="new-task-button"
              >
                <Swords className="w-5 h-5 mr-2" />
                ASSEGNA NUOVO ORDINE
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-xl text-white flex items-center gap-2">
                  <Skull className="w-6 h-6 text-amber-400" />
                  {isPreviewingTask ? 'Rivedi l\'Ordine' : 'Nuovo Ordine dalla Famiglia'}
                </DialogTitle>
                <DialogDescription className="text-zinc-500">
                  Crea o rivedi l'ordine per i ranghi della famiglia.
                </DialogDescription>
              </DialogHeader>

              {!isPreviewingTask ? (
                <div className="space-y-4 pt-4">
                  <div>
                    <Label htmlFor="assignee-select" className="text-sm text-zinc-400 mb-2 block flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      Chi deve eseguire l'ordine?
                    </Label>
                    <Popover open={openCombobox} onOpenChange={setOpenCombobox}>
                      <PopoverTrigger asChild>
                        <Button
                          id="assignee-select"
                          variant="outline"
                          role="combobox"
                          aria-expanded={openCombobox}
                          className="w-full justify-between bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700 font-normal"
                        >
                          {selectedMembers.length > 0
                            ? `${selectedMembers.length} soldati selezionati`
                            : "Seleziona i soldati..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0 bg-zinc-900 border-zinc-800">
                        <Command className="bg-zinc-900 text-white">
                          <CommandInput placeholder="Cerca soldato..." className="h-9" />
                          <CommandEmpty>Nessun soldato trovato.</CommandEmpty>
                          <CommandGroup>
                            <CommandList>
                              {members.map((member) => (
                                <CommandItem
                                  key={member.id}
                                  value={member.name}
                                  onSelect={() => toggleMemberSelection(member.id)}
                                  className="text-white hover:bg-zinc-800 cursor-pointer"
                                >
                                  <div
                                    className={cn(
                                      "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                      selectedMembers.includes(member.id)
                                        ? "bg-primary text-primary-foreground"
                                        : "opacity-50 [&_svg]:invisible"
                                    )}
                                  >
                                    <Check className={cn("h-4 w-4")} />
                                  </div>
                                  <span>{member.name}</span>
                                </CommandItem>
                              ))}
                            </CommandList>
                          </CommandGroup>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <Label htmlFor="create-task-title" className="text-sm text-zinc-400 mb-2 block">Titolo dell'ordine</Label>
                    <Input
                      id="create-task-title"
                      placeholder="Cosa deve fare..."
                      value={newTask.title}
                      onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                      className="bg-zinc-800 border-zinc-700 text-white font-bold"
                      data-testid="task-title-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="create-task-desc" className="text-sm text-zinc-400 mb-2 block">Descrizione (opzionale)</Label>
                    <Textarea
                      id="create-task-desc"
                      placeholder="Dettagli aggiuntivi..."
                      value={newTask.description}
                      onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                      className="bg-zinc-800 border-zinc-700 text-white min-h-[80px]"
                      data-testid="task-description-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="create-task-date" className="text-sm text-zinc-400 mb-2 block flex items-center gap-2">
                      <Timer className="w-4 h-4" />
                      Scadenza
                    </Label>
                    <Input
                      id="create-task-date"
                      type="date"
                      value={newTask.due_date}
                      onChange={(e) => setNewTask(prev => ({ ...prev, due_date: e.target.value }))}
                      className="bg-zinc-800 border-zinc-700 text-white"
                      data-testid="task-due-date-input"
                    />
                  </div>

                  {/* Links Section */}
                  <div className="bg-zinc-800/50 p-4 rounded-xl border border-zinc-700/50">
                    <Label htmlFor="create-task-url" className="text-sm text-zinc-400 mb-2 block flex items-center gap-2">
                      <LinkIcon className="w-4 h-4" />
                      Link e Riferimenti
                    </Label>

                    <div className="flex gap-2 mb-2">
                      <Input
                        id="create-task-url"
                        placeholder="URL (https://...)"
                        value={linkInput.url}
                        onChange={(e) => setLinkInput(prev => ({ ...prev, url: e.target.value }))}
                        className="bg-zinc-800 border-zinc-700 text-white flex-1"
                      />
                      <Input
                        placeholder="Etichetta (es. Video)"
                        value={linkInput.label}
                        onChange={(e) => setLinkInput(prev => ({ ...prev, label: e.target.value }))}
                        className="bg-zinc-800 border-zinc-700 text-white w-1/3"
                      />
                      <Button onClick={addLink} type="button" size="icon" className="bg-zinc-700 hover:bg-zinc-600">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="space-y-2">
                      {newTask.links && newTask.links.map((linkStr, idx) => {
                        const [label, url] = linkStr.split('|');
                        return (
                          <div key={idx} className="flex items-center justify-between bg-zinc-900/50 px-3 py-2 rounded border border-zinc-700 text-xs">
                            <div className="flex items-center gap-2 truncate">
                              <ExternalLink className="w-3 h-3 text-blue-400" />
                              <span className="text-zinc-300 font-bold">{label}</span>
                              <span className="text-zinc-600 truncate max-w-[150px]">{url}</span>
                            </div>
                            <button onClick={() => removeLink(idx)} className="text-zinc-500 hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Email Section */}
                  <div className="bg-zinc-800/50 p-4 rounded-xl border border-zinc-700/50 mt-4">
                    <Label htmlFor="create-task-email" className="text-sm text-zinc-400 mb-2 block flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      Riferimenti Email
                    </Label>
                    <div className="flex gap-2 mb-2">
                      <Input
                        id="create-task-email"
                        placeholder="Oggetto email o riferimento..."
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white flex-1"
                      />
                      <Button onClick={() => addEmail(false)} type="button" size="icon" className="bg-zinc-700 hover:bg-zinc-600">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {newTask.emails && newTask.emails.map((email, idx) => (
                        <Badge key={idx} variant="secondary" className="bg-zinc-900 border-zinc-700 text-zinc-300 gap-1 pr-1">
                          {email}
                          <button onClick={() => removeEmail(idx, false)} className="hover:text-red-400">
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <Button
                    className="w-full bg-amber-600 hover:bg-amber-500 text-white font-bold py-6 text-lg mt-6"
                    onClick={() => setIsPreviewingTask(true)}
                    disabled={selectedMembers.length === 0 || !newTask.title}
                  >
                    CONTINUA →
                  </Button>
                </div>
              ) : (
                <div className="space-y-6 pt-4">
                  <div className="bg-amber-950/20 border border-amber-500/20 rounded-2xl p-6 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-2 opacity-10">
                      <Skull className="w-20 h-20" />
                    </div>
                    <div className="relative z-10">
                      <div className="flex items-center gap-2 mb-4">
                        <Badge className="bg-amber-500 text-black font-bold">ORDINE ESECUTIVO</Badge>
                        <span className="text-xs text-zinc-500">per: {getAssigneeNames()}</span>
                      </div>
                      <h3 className="text-2xl font-black text-white mb-2">{newTask.title}</h3>
                      {newTask.description && (
                        <p className="text-sm text-zinc-400 mb-4 bg-black/40 p-3 rounded-lg border-l-2 border-amber-500">
                          {newTask.description}
                        </p>
                      )}
                      <div className="flex items-center gap-2 text-red-500 font-bold mb-4">
                        <Timer className="w-5 h-5" />
                        <span>SCADENZA: {newTask.due_date ? format(parseISO(newTask.due_date), 'dd MMMM yyyy', { locale: it }) : 'Nessuna'}</span>
                      </div>

                      {newTask.emails && newTask.emails.length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs text-zinc-500 mb-2 font-bold uppercase">Riferimenti Email:</p>
                          <div className="flex flex-wrap gap-2">
                            {newTask.emails.map((e, i) => (
                              <Badge key={i} className="bg-zinc-800 text-zinc-400 text-[10px]">{e}</Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {newTask.links && newTask.links.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-amber-500/20">
                          <p className="text-xs text-zinc-500 mb-2 font-bold uppercase">Allegati:</p>
                          <div className="space-y-1">
                            {newTask.links.map((l, i) => (
                              <div key={i} className="flex items-center gap-2 text-xs text-amber-400/80">
                                <LinkIcon className="w-3 h-3" />
                                <span>{l.split('|')[0]}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="flex-1 border-zinc-700 text-zinc-400 hover:text-white"
                      onClick={() => setIsPreviewingTask(false)}
                    >
                      CORREGGI
                    </Button>
                    <Button
                      className="flex-2 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white font-bold py-6 px-10 text-lg shadow-lg shadow-amber-500/20"
                      onClick={handleCreateTask}
                      data-testid="create-task-button"
                    >
                      CONFERMA E INVIA 🤌
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Edit Task Dialog */}
          <Dialog open={isEditing} onOpenChange={setIsEditing}>
            <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-white flex items-center gap-2">
                  <Target className="w-6 h-6 text-amber-500" />
                  MODIFICA ORDINE 🤌
                </DialogTitle>
                <DialogDescription className="text-zinc-500">
                  Apporta modifiche all'ordine esecutivo selezionato.
                </DialogDescription>
              </DialogHeader>

              {editingTask && (
                <div className="space-y-6 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="edit-task-title" className="text-sm text-zinc-400 mb-2 block">Titolo dell'ordine</Label>
                        <Input
                          id="edit-task-title"
                          placeholder="Cosa deve fare..."
                          value={editingTask.title}
                          onChange={(e) => setEditingTask(prev => ({ ...prev, title: e.target.value }))}
                          className="bg-zinc-800 border-zinc-700 text-white font-bold"
                        />
                      </div>

                      <div>
                        <Label htmlFor="edit-task-desc" className="text-sm text-zinc-400 mb-2 block">Descrizione (opzionale)</Label>
                        <Textarea
                          id="edit-task-desc"
                          placeholder="Dettagli aggiuntivi..."
                          value={editingTask.description || ''}
                          onChange={(e) => setEditingTask(prev => ({ ...prev, description: e.target.value }))}
                          className="bg-zinc-800 border-zinc-700 text-white min-h-[100px]"
                        />
                      </div>

                      <div>
                        <Label htmlFor="edit-task-date" className="text-sm text-zinc-400 mb-2 block flex items-center gap-2">
                          <Timer className="w-4 h-4" />
                          Scadenza
                        </Label>
                        <Input
                          id="edit-task-date"
                          type="date"
                          value={editingTask.due_date || ''}
                          onChange={(e) => setEditingTask(prev => ({ ...prev, due_date: e.target.value }))}
                          className="bg-zinc-800 border-zinc-700 text-white"
                        />
                      </div>
                    </div>

                    <div className="space-y-4">
                      {/* Edit Email References */}
                      <div className="bg-zinc-800/50 p-4 rounded-xl border border-zinc-700/50">
                        <Label htmlFor="edit-task-email" className="text-sm text-zinc-400 mb-2 block flex items-center gap-2">
                          <Send className="w-4 h-4" />
                          Riferimenti Email
                        </Label>
                        <div className="flex gap-2 mb-2">
                          <Input
                            id="edit-task-email"
                            placeholder="Oggetto email..."
                            value={emailInput}
                            onChange={(e) => setEmailInput(e.target.value)}
                            className="bg-zinc-800 border-zinc-700 text-white flex-1"
                            onKeyDown={(e) => e.key === 'Enter' && addEmail(true)}
                          />
                          <Button onClick={() => addEmail(true)} type="button" size="icon" className="bg-zinc-700 hover:bg-zinc-600">
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {editingTask.emails && editingTask.emails.map((email, idx) => (
                            <Badge key={idx} variant="secondary" className="bg-zinc-900 border-zinc-700 text-zinc-300 gap-1 pr-1">
                              {email}
                              <button onClick={() => removeEmail(idx, true)} className="hover:text-red-400">
                                <X className="w-3 h-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div className="p-4 bg-amber-500/10 rounded-xl border border-amber-500/20">
                        <p className="text-xs text-amber-500 font-bold mb-1 italic">IL DON DICE:</p>
                        <p className="text-sm text-zinc-300 italic">"{editingTask.don_threat}"</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4 border-t border-zinc-800">
                    <Button
                      variant="outline"
                      className="flex-1 border-zinc-700 text-zinc-400"
                      onClick={() => setIsEditing(false)}
                    >
                      ANNULLA
                    </Button>
                    <Button
                      className="flex-1 bg-amber-600 hover:bg-amber-500 text-white font-bold"
                      onClick={() => {
                        console.log("Updating task:", editingTask);
                        handleUpdateTask();
                      }}
                    >
                      SALVA MODIFICHE 🤌
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* New Member Dialog */}
          <Dialog open={showNewMember} onOpenChange={setShowNewMember}>
            <DialogTrigger asChild>
              <Button
                size="lg"
                variant="outline"
                className="border-green-500/30 text-green-400 hover:bg-green-500/10 hover:border-green-500/50 font-bold px-6 py-6 rounded-xl"
                data-testid="new-member-button"
              >
                <UserPlus className="w-5 h-5 mr-2" />
                RECLUTA NUOVO SOLDATO
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-xl text-white flex items-center gap-2">
                  <UserPlus className="w-6 h-6 text-green-400" />
                  Recluta un Nuovo Membro 🇫🇷
                </DialogTitle>
                <DialogDescription className="text-zinc-500">
                  Aggiungi un nuovo soldato ai ranghi della famiglia.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="bg-green-950/30 border border-green-500/20 rounded-lg p-3 text-sm text-green-400/80">
                  <p>⚠️ Il Don è sempre alla ricerca di nuovi talenti!</p>
                  <p className="text-xs mt-1 text-green-400/60">Il nuovo membro potrà accedere immediatamente con la sua email.</p>
                </div>

                <div>
                  <Label htmlFor="new-member-name" className="text-sm text-zinc-400 mb-2 block">Nome del nuovo soldato</Label>
                  <Input
                    id="new-member-name"
                    placeholder="Come si chiama..."
                    value={newMember.name}
                    onChange={(e) => setNewMember(prev => ({ ...prev, name: e.target.value }))}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="new-member-name"
                  />
                </div>

                <div>
                  <Label htmlFor="new-member-email" className="text-sm text-zinc-400 mb-2 block">Email</Label>
                  <Input
                    id="new-member-email"
                    type="email"
                    placeholder="email@esempio.com"
                    value={newMember.email}
                    onChange={(e) => setNewMember(prev => ({ ...prev, email: e.target.value }))}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="new-member-email"
                  />
                </div>

                <Button
                  className="w-full bg-green-600 hover:bg-green-500 text-white font-bold py-6 text-lg"
                  onClick={handleCreateMember}
                  disabled={!newMember.name || !newMember.email}
                  data-testid="create-member-button"
                >
                  <UserPlus className="w-5 h-5 mr-2" />
                  ARRUOLA NELLA FAMIGLIA 🤌🇫🇷
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          {/* View Toggles */}
          <div className="flex bg-zinc-900 p-1 rounded-xl border border-zinc-800 ml-auto">
            <Button
              variant={viewMode === 'kanban' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('kanban')}
              className={cn(
                "rounded-lg px-4 font-bold flex items-center gap-2",
                viewMode === 'kanban' ? "bg-amber-500 text-black hover:bg-amber-400 transition-all" : "text-zinc-400 hover:text-white"
              )}
            >
              <KanbanIcon className="w-4 h-4" />
              STATUS
            </Button>
            <Button
              variant={viewMode === 'planning' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('planning')}
              className={cn(
                "rounded-lg px-4 font-bold flex items-center gap-2",
                viewMode === 'planning' ? "bg-amber-500 text-black hover:bg-amber-400 transition-all" : "text-zinc-400 hover:text-white"
              )}
            >
              <CalendarDays className="w-4 h-4" />
              PLANNING
            </Button>
            <Button
              variant={viewMode === 'team' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('team')}
              className={cn(
                "rounded-lg px-4 font-bold flex items-center gap-2",
                viewMode === 'team' ? "bg-amber-500 text-black hover:bg-amber-400 transition-all" : "text-zinc-400 hover:text-white"
              )}
            >
              <Users className="w-4 h-4" />
              TEAM
            </Button>
            <Button
              variant={viewMode === 'calendar' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('calendar')}
              className={cn(
                "rounded-lg px-4 font-bold flex items-center gap-2",
                viewMode === 'calendar' ? "bg-amber-500 text-black hover:bg-amber-400 transition-all" : "text-zinc-400 hover:text-white"
              )}
            >
              <CalendarRange className="w-4 h-4" />
              CALENDARIO
            </Button>
          </div>
        </div>

        {/* Tasks Grid */}
        {/* Tasks Grid / Planning View */}
        {viewMode === 'kanban' ? (
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Pending */}
              <Droppable droppableId="pending">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={cn(
                      "flex flex-col gap-4 p-2 rounded-xl transition-colors",
                      snapshot.isDraggingOver && "bg-zinc-800/20"
                    )}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      <h3 className="font-display font-bold text-white uppercase tracking-wider text-sm">In Attesa</h3>
                      <Badge variant="destructive">{pendingTasks.length}</Badge>
                    </div>
                    <div className="space-y-3 min-h-[200px]">
                      {pendingTasks.map((task, index) => (
                        <TaskCard
                          key={task.id}
                          task={task}
                          index={index}
                          onDelete={deleteTask}
                          onUpdateStatus={updateTaskStatus}
                          onPause={setPausingTask}
                          onEdit={handleEditClick}
                        />
                      ))}
                      {provided.placeholder}
                      {pendingTasks.length === 0 && !snapshot.isDraggingOver && (
                        <p className="text-sm text-zinc-600 text-center py-8">Nessun ordine in attesa</p>
                      )}
                    </div>
                  </div>
                )}
              </Droppable>

              {/* In Progress */}
              <Droppable droppableId="in_progress">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={cn(
                      "flex flex-col gap-4 p-2 rounded-xl transition-colors",
                      snapshot.isDraggingOver && "bg-zinc-800/20"
                    )}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-5 h-5 text-amber-500" />
                      <h3 className="font-display font-bold text-white uppercase tracking-wider text-sm">In Corso</h3>
                      <Badge className="bg-amber-500/20 text-amber-400">{inProgressTasks.length}</Badge>
                    </div>
                    <div className="space-y-3 min-h-[200px]">
                      {inProgressTasks.map((task, index) => (
                        <TaskCard
                          key={task.id}
                          task={task}
                          index={index}
                          onDelete={deleteTask}
                          onUpdateStatus={updateTaskStatus}
                          onPause={setPausingTask}
                          onEdit={handleEditClick}
                        />
                      ))}
                      {provided.placeholder}
                      {inProgressTasks.length === 0 && !snapshot.isDraggingOver && (
                        <p className="text-sm text-zinc-600 text-center py-8">Nessun ordine in corso</p>
                      )}
                    </div>
                  </div>
                )}
              </Droppable>

              {/* Completed */}
              <Droppable droppableId="completed">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={cn(
                      "flex flex-col gap-4 p-2 rounded-xl transition-colors",
                      snapshot.isDraggingOver && "bg-zinc-800/20"
                    )}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                      <h3 className="font-display font-bold text-white uppercase tracking-wider text-sm">Completati</h3>
                      <Badge className="bg-green-500/20 text-green-400">{completedTasks.length}</Badge>
                    </div>
                    <div className="space-y-3 min-h-[200px] opacity-60">
                      {completedTasks.slice(0, 10).map((task, index) => (
                        <TaskCard
                          key={task.id}
                          task={task}
                          index={index}
                          onDelete={deleteTask}
                          onUpdateStatus={updateTaskStatus}
                          onPause={setPausingTask}
                          onEdit={handleEditClick}
                          isCompleted
                        />
                      ))}
                      {provided.placeholder}
                      {completedTasks.length === 0 && !snapshot.isDraggingOver && (
                        <p className="text-sm text-zinc-600 text-center py-8">Nessun ordine completato</p>
                      )}
                    </div>
                  </div>
                )}
              </Droppable>
            </div>
          </DragDropContext>
        ) : viewMode === 'planning' ? (
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="space-y-8">
              {Object.entries(groupTasksByDate(safeTasks)).map(([group, groupTasks]) => {
                if (groupTasks.length === 0 && group !== 'today' && group !== 'tomorrow') return null;

                const groupLabels = {
                  expired: { label: 'Scaduti', icon: AlertTriangle, color: 'text-red-500' },
                  today: { label: 'Oggi', icon: Clock, color: 'text-amber-500' },
                  tomorrow: { label: 'Domani', icon: CalendarDays, color: 'text-blue-500' },
                  week: { label: 'Prossimi Giorni', icon: Calendar, color: 'text-zinc-400' },
                  future: { label: 'Futuro', icon: Swords, color: 'text-zinc-600' },
                  noDate: { label: 'Senza Scadenza', icon: Shield, color: 'text-zinc-700' }
                };

                const config = groupLabels[group];

                return (
                  <div key={group} className="space-y-4">
                    <div className="flex items-center gap-2 border-l-4 border-zinc-800 pl-4 py-1">
                      {config && <config.icon className={cn("w-5 h-5", config.color)} />}
                      <h3 className="font-display font-bold text-white uppercase tracking-widest text-sm">
                        {config?.label || group}
                      </h3>
                      <Badge variant="outline" className="border-zinc-800 text-zinc-500">{groupTasks.length}</Badge>
                    </div>

                    <Droppable droppableId={group}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={cn(
                            "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 min-h-[40px] rounded-xl transition-colors",
                            snapshot.isDraggingOver && "bg-zinc-800/20"
                          )}
                        >
                          {groupTasks.map((task, index) => (
                            <TaskCard
                              key={task.id}
                              task={task}
                              index={index}
                              onDelete={deleteTask}
                              onUpdateStatus={updateTaskStatus}
                              onEdit={handleEditClick}
                              minimal
                            />
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                );
              })}
            </div>
          </DragDropContext>
        ) : viewMode === 'calendar' ? (
          <ScheduleCalendar
            tasks={safeTasks}
            members={members}
            user={user}
            apiUpdateTask={apiUpdateTask}
            createTask={createTask}
          />
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Users className="w-6 h-6 text-amber-500" />
                I SOLDATI DELLA FAMIGLIA
              </h2>
              <Badge variant="outline" className="border-amber-500/30 text-amber-500">
                {members.length} Ranghi Attivi
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {members.map((member) => {
                const hoursToday = stats?.today_by_user?.[member.name] || 0;
                const isSlacking = hoursToday === 0;

                return (
                  <Card key={member.id} className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-all">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center font-bold text-amber-500 border border-amber-500/20">
                            {member.name.charAt(0)}
                          </div>
                          <div>
                            <h4 className="font-bold text-white">{member.name}</h4>
                            <p className="text-xs text-zinc-500">{member.email}</p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-zinc-400">Attività odierna:</span>
                          <span className={cn(
                            "font-black",
                            isSlacking ? "text-red-500" : "text-green-500"
                          )}>
                            {hoursToday.toFixed(1)}h
                          </span>
                        </div>

                        {isSlacking && member.role !== 'admin' && (
                          <Button
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/push/minaccia/${member.id}`);
                                alert(`Minaccia inviata a ${member.name}! 🤌`);
                              } catch (err) {
                                console.error("Failed to send minaccia:", err);
                              }
                            }}
                            className="w-full bg-red-600/10 hover:bg-red-600 border border-red-600/30 text-red-500 hover:text-white transition-all text-xs font-bold"
                          >
                            <Skull className="w-3 h-3 mr-2" />
                            RICHIAMA ALL'ORDINE
                          </Button>
                        )}

                        {!isSlacking && (
                          <div className="flex items-center justify-center p-2 rounded-lg bg-green-950/20 border border-green-500/20 text-green-500 text-[10px] font-bold uppercase tracking-wider">
                            <Check className="w-3 h-3 mr-1" />
                            Soldato Operativo
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </main >

      {/* Pending Reason Modal */}
      <PendingReasonModal
        task={pausingTask}
        isOpen={!!pausingTask}
        onClose={() => setPausingTask(null)}
        onConfirm={handlePauseConfirm}
      />

      {/* Footer */}
      < footer className="border-t border-zinc-800 p-6 text-center mt-8" >
        <p className="text-2xl mb-2">👑🇫🇷</p>
        <p className="text-zinc-500 text-sm font-display">Don Doit © 2025 - La Famiglia QuiteBold</p>
        <p className="text-xs text-zinc-700 mt-1 italic">"Chi comanda non chiede, ordina... avec élégance!"</p>
      </footer >
    </div >
  );
};

export default ManagerDashboard;
